import { Ce } from "@cimo/environment/dist/src/Main";

// Source
import StorePreference from "./store/Preference";
import * as ModelPreference from "./model/Preference";
import * as ModelHelperSrc from "./model/HelperSrc";
import * as ModelUser from "./model/User";
import * as ModelRole from "./model/Role";
import * as ModelRoute from "./model/Route";
import * as ModelSetting from "./model/Setting";
import ControllerAlert from "./controller/Alert";

export const ENV_NAME = Ce.checkVariable("ENV_NAME") || (process.env.ENV_NAME as string);

Ce.loadFile(`./env/${ENV_NAME}.env`);

export const DOMAIN = Ce.checkVariable("DOMAIN") || (process.env.DOMAIN as string);
export const TIME_ZONE = Ce.checkVariable("TIME_ZONE") || (process.env.TIME_ZONE as string);
export const LANG = Ce.checkVariable("LANG") || (process.env.LANG as string);
export const SERVER_PORT = Ce.checkVariable("SERVER_PORT") || (process.env.SERVER_PORT as string);
export const PATH_ROOT = Ce.checkVariable("PATH_ROOT");
export const NAME = Ce.checkVariable("REACT_APP_NAME") || (process.env.REACT_APP_NAME as string);
export const LABEL = Ce.checkVariable("REACT_APP_LABEL") || (process.env.REACT_APP_LABEL as string);
export const IS_DEBUG = Ce.checkVariable("REACT_APP_IS_DEBUG") || (process.env.REACT_APP_IS_DEBUG as string);
export const NODE_ENV = Ce.checkVariable("REACT_APP_NODE_ENV") || (process.env.REACT_APP_NODE_ENV as string);
export const URL_ROOT = Ce.checkVariable("REACT_APP_URL_ROOT") || (process.env.REACT_APP_URL_ROOT as string);
export const URL_BACKEND = Ce.checkVariable("REACT_APP_URL_BACKEND") || (process.env.REACT_APP_URL_BACKEND as string);
export const URL_AD_LOGIN = Ce.checkVariable("REACT_APP_URL_AD_LOGIN") || (process.env.REACT_APP_URL_AD_LOGIN as string);
export const URL_AD_REDIRECT = Ce.checkVariable("REACT_APP_URL_AD_REDIRECT") || (process.env.REACT_APP_URL_AD_REDIRECT as string);
export const PATH_CERTIFICATE_KEY = Ce.checkVariable("REACT_APP_PATH_CERTIFICATE_KEY");
export const PATH_CERTIFICATE_CRT = Ce.checkVariable("REACT_APP_PATH_CERTIFICATE_CRT");
export const PATH_CERTIFICATE_PEM = Ce.checkVariable("REACT_APP_PATH_CERTIFICATE_PEM");

Ce.loadFile(`./env/${ENV_NAME}.secret.env`);

export const DOCUMENT_PREVIEW_DRAFT_TIMEOUT = 1 * 60 * 1000;
export const STATUS_TIMEOUT = 3000;
export const REQUEST_TIMEOUT = 60000;
export const REQUEST_IS_ENCODED = false;

export const localeConfiguration: Record<string, { locale: string; currency: string; dateFormat: string }> = {
    // Asia
    jp: { locale: "ja-JP", currency: "JPY", dateFormat: "a" },
    cn: { locale: "zh-CN", currency: "CNY", dateFormat: "a" },
    tw: { locale: "zh-TW", currency: "TWD", dateFormat: "a" },
    kr: { locale: "ko-KR", currency: "KRW", dateFormat: "a" },
    in: { locale: "hi-IN", currency: "INR", dateFormat: "a" },
    th: { locale: "th-TH", currency: "THB", dateFormat: "a" },
    // Europe
    it: { locale: "it-IT", currency: "EUR", dateFormat: "b" },
    fr: { locale: "fr-FR", currency: "EUR", dateFormat: "b" },
    de: { locale: "de-DE", currency: "EUR", dateFormat: "b" },
    es: { locale: "es-ES", currency: "EUR", dateFormat: "b" },
    pt: { locale: "pt-PT", currency: "EUR", dateFormat: "b" },
    nl: { locale: "nl-NL", currency: "EUR", dateFormat: "b" },
    ru: { locale: "ru-RU", currency: "RUB", dateFormat: "b" },
    pl: { locale: "pl-PL", currency: "PLN", dateFormat: "b" },
    sv: { locale: "sv-SE", currency: "SEK", dateFormat: "b" },
    // America
    us: { locale: "en-US", currency: "USD", dateFormat: "c" },
    mx: { locale: "es-MX", currency: "MXN", dateFormat: "c" },
    br: { locale: "pt-BR", currency: "BRL", dateFormat: "c" },
    ca: { locale: "fr-CA", currency: "CAD", dateFormat: "c" },
    // Africa
    ke: { locale: "sw-KE", currency: "KES", dateFormat: "c" },
    za: { locale: "af-ZA", currency: "ZAR", dateFormat: "c" },
    eg: { locale: "ar-EG", currency: "EGP", dateFormat: "c" },
    // Oceania
    au: { locale: "en-AU", currency: "AUD", dateFormat: "c" },
    nz: { locale: "mi-NZ", currency: "NZD", dateFormat: "c" }
};

const populateFieldHelper = <T>(element: HTMLInputElement, data?: T): void => {
    const elementForm = findElementParent(element, "form");

    if (elementForm) {
        const elementErrorList = elementForm.querySelectorAll<HTMLInputElement>(".error");
        const elementFieldContainer = findElementParent(element, "field_container");

        elementForm.classList.remove("error");

        if (elementErrorList && elementErrorList.length > 0) {
            for (let a = 0; a < elementErrorList.length; a++) {
                elementForm.classList.add("error");
            }
        }

        if (elementFieldContainer) {
            const elementFieldHelper = elementFieldContainer.querySelector<HTMLElement>(".field_helper");

            if (data) {
                let message = "";

                if (typeof data !== "string") {
                    const validationForm = data[element.name] as ModelHelperSrc.IvalidationForm;
                    message = validationForm.error_msg;
                } else {
                    message = data;
                }

                if (elementFieldHelper) {
                    elementFieldHelper.innerHTML = message;
                }
            } else {
                if (elementFieldHelper) {
                    elementFieldHelper.innerHTML = "";
                }
            }
        }
    }
};

export type Color = "success" | "info" | "warning" | "error";

export const writeLog = (tag: string, value: string | Record<string, unknown> | Error): void => {
    if (IS_DEBUG === "true") {
        // eslint-disable-next-line no-console
        console.log(`WriteLog => ${tag}: `, value);
    }
};

export const writeCookie = <T>(tag: string, value: T, expire = "", httpOnly = "", path = "/"): void => {
    const encodedData = window.btoa(encodeURIComponent(JSON.stringify(value)));

    document.cookie = `${LABEL}_${tag}=${encodedData};expires=${expire};${httpOnly};path=${path};Secure`;
};

export const readCookie = <T>(tag: string): T | undefined => {
    let result: T | undefined;

    const name = escapeRegExp(`${LABEL}_${tag}`);
    const resultMatch = document.cookie.match(new RegExp(`${name}=([^;]+)`));

    if (resultMatch) {
        let cookie = resultMatch[1];

        if (isBase64(cookie.replaceAll('"', ""))) {
            cookie = window.atob(cookie.replaceAll('"', ""));
        }

        const decodeUriCookie = decodeURIComponent(cookie);

        if (isJson(decodeUriCookie)) {
            result = JSON.parse(decodeUriCookie) as T;
        } else {
            result = decodeUriCookie as T;
        }
    }

    return result;
};

export const removeCookie = (tag: string): void => {
    document.cookie = `${LABEL}_${tag}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

export const writeStorage = <T>(tag: string, value: T): void => {
    const encodedData = window.btoa(encodeURIComponent(JSON.stringify(value)));

    localStorage.setItem(`${LABEL}_${tag}`, encodedData);
};

export const readStorage = <T>(tag: string): T | undefined => {
    let result: T | undefined;

    const storage = localStorage.getItem(`${LABEL}_${tag}`);

    if (storage) {
        result = JSON.parse(decodeURIComponent(window.atob(storage))) as T;
    }

    return result;
};

export const removeStorage = (tag: string): void => {
    localStorage.removeItem(`${LABEL}_${tag}`);
};

export const routerHistoryPush = (nextUrl: string, parameterListValue?: Record<string, unknown>, title = "", soft = false): void => {
    let url = nextUrl;

    const [path, queryString] = url.split("?");
    const queryStringCleanedList: string[] = [];

    if (queryString) {
        const params = queryString.split("&");

        params.forEach((param) => {
            const [key, value] = param.split("=");

            const keyCleaned = encodeURIComponent(
                decodeURIComponent(key.replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;"))
            );

            if (value) {
                const valueCleaned = encodeURIComponent(
                    decodeURIComponent(value.replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;"))
                );

                queryStringCleanedList.push(`${keyCleaned}=${valueCleaned}`);
            } else {
                queryStringCleanedList.push(keyCleaned);
            }
        });
    }

    const pathCleaned = path.replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
    const urlCleaned = pathCleaned + (queryStringCleanedList.length > 0 ? "?" + queryStringCleanedList.join("&") : "");

    window.history.pushState(
        {
            prevUrl: window.location.pathname,
            parameterList: parameterListValue
        },
        title,
        urlCleaned
    );

    if (!soft) {
        window.location.href = urlCleaned;
    }
};

export const routerHistoryPopState = (paramterListValue?: Record<string, unknown>): void => {
    window.addEventListener("popstate", (event) => {
        if (event.state) {
            const historyState = event.state as Record<string, unknown> | undefined;

            if (historyState) {
                historyState.parameterList = paramterListValue;
            }
        }
    });
};

export const urlVariable = (name: string): string => {
    const windowLocationSearchCleaned = window.location.search
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

    const urlParameter = new URLSearchParams(windowLocationSearchCleaned);

    return urlParameter.get(name) || "";
};

export const findElementParent = (element: HTMLElement, className: string): HTMLElement | null => {
    if (!element.parentNode || element.parentNode === document) {
        return null;
    }

    if (element && element.classList.contains(className)) {
        return element;
    }

    return findElementParent(element.parentNode as HTMLElement, className);
};

export const checkRegex = (
    element: HTMLInputElement | HTMLTextAreaElement,
    elementValue: string,
    regex: string | undefined,
    message: string | undefined
): void => {
    if (element instanceof HTMLInputElement && element.type !== "checkbox" && element.type !== "radio") {
        const elementFieldContainer = findElementParent(element, "field_container");

        if (elementFieldContainer) {
            const elementFieldValue = elementFieldContainer.querySelector<HTMLElement>(".field_value");

            let isError = false;

            if (regex) {
                const regularExpression = new RegExp(regex);

                if (
                    (element.hasAttribute("required") && elementValue !== "" && !regularExpression.test(elementValue)) ||
                    (!element.hasAttribute("required") && elementValue !== "" && !regularExpression.test(elementValue))
                ) {
                    isError = true;
                }
            }

            if (element.hasAttribute("required") && elementValue === "") {
                isError = true;
            }

            if (isError) {
                if (elementFieldValue) {
                    elementFieldValue.classList.add("error");
                }

                populateFieldHelper(element, message);
            } else {
                if (elementFieldValue) {
                    elementFieldValue.classList.remove("error");
                }

                populateFieldHelper(element);
            }
        }
    } else if (element instanceof HTMLInputElement && element.type === "checkbox") {
        const elementFieldContainer = findElementParent(element, "field_container");

        if (elementFieldContainer) {
            const elementFieldValue = elementFieldContainer.querySelector<HTMLElement>(".field_value");

            if (elementFieldValue) {
                elementFieldValue.classList.remove("error");
            }

            populateFieldHelper(element);
        }
    } else if (element instanceof HTMLInputElement && element.type === "radio") {
        const elementRadioParent = findElementParent(element, "MuiFormGroup-root");

        if (elementRadioParent) {
            const elementRadioNotCheckedList = elementRadioParent.querySelectorAll<HTMLInputElement>("input[required]:not(:checked)");

            for (const elementRadioNotChecked of elementRadioNotCheckedList) {
                const radioNotCheckedFieldValue = findElementParent(elementRadioNotChecked, "field_value");

                if (radioNotCheckedFieldValue) {
                    radioNotCheckedFieldValue.classList.remove("error");
                }
            }

            populateFieldHelper(element);
        }
    }
};

export const checkRegexSelect = (elementName: string, elementValue: string): void => {
    const inputElement = document.querySelector<HTMLInputElement>(`[name='${elementName}']`);

    if (inputElement instanceof HTMLInputElement) {
        const elementSelectParent = findElementParent(inputElement, "field_value");

        if (elementSelectParent && elementValue !== "") {
            elementSelectParent.classList.remove("error");
        }

        populateFieldHelper(inputElement);
    }
};

export const checkForm = <T>(element: HTMLElement, data: T, isSkip = false): boolean => {
    let result = true;

    const elementForm = findElementParent(element, "form");

    if (elementForm) {
        if (isSkip === false || (elementForm.classList.contains("error") && isSkip === true)) {
            const elementInput1List = elementForm.querySelectorAll<HTMLInputElement>("input[required]:not([type='radio'])");

            for (const elementInput1 of elementInput1List) {
                const inputFieldValue = findElementParent(elementInput1, "field_value");

                if (
                    (elementInput1.type !== "checkbox" && elementInput1.value === "") ||
                    (elementInput1.type === "checkbox" && !elementInput1.checked)
                ) {
                    result = false;

                    if (inputFieldValue) {
                        inputFieldValue.classList.add("error");
                    }
                } else {
                    if (inputFieldValue) {
                        inputFieldValue.classList.remove("error");
                    }
                }
            }

            const elementInput2List = elementForm.querySelectorAll<HTMLInputElement>("input[required][type='radio']");

            for (const elementInput2 of elementInput2List) {
                const radioFieldValue = findElementParent(elementInput2, "field_value");
                const elementRadioParent = findElementParent(elementInput2, "MuiFormGroup-root");

                if (elementRadioParent) {
                    const elementRadioChecked = elementRadioParent.querySelector<HTMLInputElement>("input[required]:checked");

                    if (!elementRadioChecked) {
                        result = false;

                        if (radioFieldValue) {
                            radioFieldValue.classList.add("error");
                        }
                    } else {
                        if (radioFieldValue) {
                            radioFieldValue.classList.remove("error");
                        }
                    }
                }
            }

            const elementInput3List = elementForm.querySelectorAll<HTMLInputElement>("input");

            for (const elementInput3 of elementInput3List) {
                const inputFieldValue = findElementParent(elementInput3, "field_value");

                if (inputFieldValue && inputFieldValue.classList.contains("error")) {
                    populateFieldHelper(elementInput3, data);
                }
            }
        }
    }

    return result;
};

export const validationFormHelper = <T>(element: HTMLElement | null, data: T): void => {
    if (element) {
        const elementForm = findElementParent(element, "form");

        if (elementForm) {
            const elementInputList = elementForm.querySelectorAll<HTMLInputElement>("input");

            for (const element of elementInputList) {
                const inputFieldValue = findElementParent(element, "field_value");

                if (inputFieldValue && inputFieldValue.classList.contains("error")) {
                    populateFieldHelper(element, data);
                }
            }
        }
    }
};

export const downloadFromBrowser = (data: string | DataView, fileName: string): void => {
    let blob = new Blob();

    if (typeof data === "string") {
        const decodedData = window.atob(data);
        const uint8Array = new Uint8Array(decodedData.length);

        for (let a = 0; a < decodedData.length; a++) {
            uint8Array[a] = decodedData.charCodeAt(a);
        }

        blob = new Blob([uint8Array], { type: "application/octet-stream" });
    } else {
        const uint8Array = new Uint8Array(data.buffer as ArrayBuffer, data.byteOffset, data.byteLength);
        blob = new Blob([uint8Array], { type: "application/octet-stream" });
    }

    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
};

export const isJson = (value: string): boolean => {
    try {
        JSON.parse(value);

        return true;
    } catch {
        return false;
    }
};

export const isBase64 = (value: string): boolean => {
    return /^[A-Za-z0-9+/]*={0,2}$/.test(value) && value.length % 4 === 0;
};

export const escapeRegExp = (value: string): string => {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

export const checkSessionTimeout = (
    routerStorageMain: ModelRoute.Idata | undefined,
    routerStorageSetting: ModelSetting.Idata | undefined,
    userData: ModelUser.Idata | undefined
): void => {
    if (routerStorageMain && routerStorageSetting && userData) {
        const tokenLifetimeMilisecond = parseInt(routerStorageSetting.token_lifetime_millisecond);

        const timeout = setTimeout(() => {
            clearTimeout(timeout);

            if (userData && userData.last_activity) {
                const lastActivity = new Date(userData.last_activity);
                const currentTime = new Date();
                const differenceTime = currentTime.getTime() - lastActivity.getTime();

                if (differenceTime > tokenLifetimeMilisecond) {
                    writeCookie("session_expired", "");

                    routerHistoryPush(routerStorageMain.logout.path);
                } else {
                    checkSessionTimeout(routerStorageMain, routerStorageSetting, userData);
                }
            }
        }, tokenLifetimeMilisecond);
    }
};

export const convertRoleToCheckbox = (storageRole: ModelRole.Idata | undefined, roleList: number[]): Record<string, never> => {
    const result = {};

    if (storageRole) {
        for (const role of Object.keys(storageRole)) {
            if (roleList.includes(storageRole[role] as number)) {
                result[`is_checkbox_${role}`] = true;
            }
        }
    }

    return result;
};

export const convertCheckboxToRole = (storageRole: ModelRole.Idata | undefined, checkboxList: Record<string, boolean>): number[] => {
    const result: number[] = [];

    if (storageRole) {
        for (const [key, value] of Object.entries(checkboxList)) {
            for (const role of Object.keys(storageRole)) {
                if (key === `is_checkbox_${role}` && value) {
                    result.push(storageRole[role] as number);

                    break;
                }
            }
        }
    }

    return result;
};

export const readSelectOptionData = (
    selectOption: ModelHelperSrc.IelementOption | undefined,
    value: string,
    mode = "display_name"
): ModelHelperSrc.IelementOption => {
    let result: ModelHelperSrc.IelementOption = { value: 0, display_name: "" };

    if (selectOption) {
        for (const option of Object.values(selectOption) as unknown as [ModelHelperSrc.IelementOption]) {
            let optionValue = "";

            if (mode === "display_name") {
                optionValue = option.display_name;
            } else if (mode === "value") {
                optionValue = option.value.toString();
            }

            if (optionValue.localeCompare(value) === 0) {
                result = option;

                break;
            }
        }
    }

    return result;
};

export const checkRole = (storageRole: ModelRole.Idata | undefined, roleNameList: string[], user: ModelUser.Idata | undefined): boolean => {
    const roleList: string[] = [];

    if (user && user.id && storageRole) {
        for (const role of Object.keys(storageRole)) {
            if (user.role.includes(storageRole[role] as number)) {
                roleList.push(role);
            }
        }
    }

    return roleList.some((roleList) => roleNameList.includes(roleList));
};

export const storageDispatchEvent = (tag: string): void => {
    window.dispatchEvent(new Event(`storage_${tag}`));
};

export const storageEventListener = (tag: string, callback: () => void): void => {
    window.addEventListener(`storage_${tag}`, () => {
        callback();
    });
};

export const localeFormat = (value: number | Date, isMonth = true, isDay = true, isTime = true): string | undefined => {
    const storePreference: ModelPreference.Istore<ModelPreference.Idata> = new StorePreference();
    const storagePreferenceData = storePreference.storageGet();

    const country = storagePreferenceData ? storagePreferenceData.country : LANG;

    if (typeof value === "number") {
        const formatOptions: Intl.NumberFormatOptions = {
            style: "decimal",
            currency: localeConfiguration[country].currency
        };

        return new Intl.NumberFormat(localeConfiguration[country].locale, formatOptions).format(value);
    } else if (value instanceof Date) {
        let formatOptions: Intl.DateTimeFormatOptions = {
            year: "numeric"
        };

        if (isMonth) {
            formatOptions.month = "numeric";
        }

        if (isDay) {
            formatOptions.day = "numeric";
        }

        if (isTime) {
            formatOptions.hour = "2-digit";
            formatOptions.minute = "2-digit";
            formatOptions.second = "2-digit";
            formatOptions.hour12 = false;
        }

        let result = new Intl.DateTimeFormat(localeConfiguration[country].locale, formatOptions).format(value);

        if (!isMonth && !isDay && !isTime) {
            result = parseInt(result).toString();
        }

        return result;
    }

    return undefined;
};

export const domObserver = (tag: string, callback: () => void, isAttribute = false): void => {
    let parameterList: MutationObserverInit = { childList: true, subtree: true };

    if (!isAttribute) {
        const element = document.querySelector(tag);

        if (element) {
            callback();

            return;
        }
    } else {
        parameterList.attributes = true;
        parameterList.attributeFilter = ["class"];
    }

    const observer = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
            if (!isAttribute) {
                if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
                    const element = document.querySelector<Element>(tag);

                    if (element) {
                        callback();

                        observer.disconnect();

                        break;
                    }
                }
            } else {
                if (mutation.type === "attributes" && mutation.attributeName === "class") {
                    const element = document.querySelector<Element>(tag);

                    if (element) {
                        callback();

                        break;
                    }
                }
            }
        }
    });

    observer.observe(document.body, parameterList);
};

export const showSelectedOptionName = (list: ModelHelperSrc.IelementOption, value: string): string => {
    let result = "";

    for (const optionData of Object.values(list)) {
        const optionDataTyped = optionData as ModelHelperSrc.IelementOption;

        if (optionDataTyped.value === parseInt(value)) {
            result = optionDataTyped.display_name;

            break;
        }
    }

    return result;
};

export const disableConsoleMessage = (text: string): void => {
    // eslint-disable-next-line no-console
    const originalError = console.error;

    // eslint-disable-next-line no-console
    console.error = (...argumentList: unknown[]) => {
        if (typeof argumentList[0] === "string" && argumentList[0].startsWith(text)) {
            return;
        }

        if (originalError) {
            originalError.apply(console, argumentList);
        }
    };
};

export const apiWrapPromise = (
    callFunction: (callback: (response: ModelHelperSrc.Iresponse | Error) => void) => void,
    dataFunction: (data: Record<string, unknown>) => void,
    controllerAlert: ControllerAlert
): Promise<void> => {
    return new Promise((resolve) => {
        callFunction((response) => {
            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    writeLog("error", response.message);
                } else {
                    controllerAlert.show("error", response.message.error, false);
                }
            } else {
                dataFunction(response.data.result);
            }

            resolve();
        });
    });
};

export const localeFromEnvName = (): string | undefined => {
    let result = ENV_NAME.split("_").pop();

    if (result === "local") {
        result = "jp";
    }

    return result;
};

export const updateInputDate = (): void => {
    const elementInputDateList = document.querySelectorAll<HTMLElement>(".field_value");

    for (const elementInputDate of elementInputDateList) {
        const elementInput = elementInputDate.querySelector<HTMLInputElement>("input");

        if (elementInput && elementInput.value !== "") {
            if (elementInputDate.classList.contains("date_time")) {
                elementInput.value = localeFormat(new Date(elementInput.value)) || "";
            } else if (elementInputDate.classList.contains("date")) {
                elementInput.value = localeFormat(new Date(elementInput.value), true, true, false) || "";
            }
        }
    }
};

export const createAutocompleteSlotProps = (
    popperClassName: string,
    offset: [number, number] = [0, 0],
    placement: "bottom-start" | "bottom" | "bottom-end" = "bottom-start"
): {
    popper: ModelHelperSrc.Ipopper;
} => {
    return {
        popper: {
            className: popperClassName,
            placement,
            modifiers: [
                {
                    name: "offset",
                    options: {
                        offset
                    }
                }
            ]
        }
    };
};
